import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import { EmptyState, Shimmer } from '@/components/ui';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Clock,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  Maximize2,
} from 'lucide-react';

import {
  getSeatAvailability,
  holdSeats,
  releaseSeatHold,
  getServiceById,
} from '@/api/apiUtils';
import { useAuth } from '@/context/AuthContext';
import { friendlyError } from '@/lib/apiError';

const STATUS_COLORS = {
  selected: '#fbbf24', // amber
  held: '#9ca3af', // gray
  booked: '#374151', // dark gray
  blocked: '#1f2937', // near-black
};

// Low enough that "fit" can show the whole venue on a phone. It was 0.4, which
// clamped a 390px screen's true fit (~0.29) *up*, rendering the map ~480px
// wide and cutting ~45px off each side.
const MIN_SCALE = 0.15;
// Touch screens: below this on-screen seat size a tap zooms toward the spot
// instead of guessing a seat — a fingertip is ~40px, so at overview scale
// (~7-10px seats) "tap a seat" was a lottery. Zooms to about TARGET px seats.
const TAP_ZOOM_SEAT_PX = 22;
const TAP_ZOOM_TARGET_PX = 30;
const MAX_SCALE = 2.5;
const ZOOM_STEP = 1.15;
const HARD_MAX_SEATS = 10;

const SeatSelection = () => {
  const { id: serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, openAuthModal } = useAuth();

  const stateFromTicketSelection = location.state ?? {};
  const initialEvent = stateFromTicketSelection.event ?? null;
  const selectedDate = stateFromTicketSelection.selectedDate ?? null;
  const selectedTime = stateFromTicketSelection.selectedTime ?? null;
  const rawEvent = stateFromTicketSelection.rawEvent ?? null;

  const [event, setEvent] = useState(initialEvent);
  const [layout, setLayout] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [selectedSeatIds, setSelectedSeatIds] = useState(new Set());
  const [hold, setHold] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [maxSeats, setMaxSeats] = useState(null);
  const [seatsModalOpen, setSeatsModalOpen] = useState(true);
  const [pendingCount, setPendingCount] = useState(2);

  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  // Latest scale/pan for zoomBy, which can fire faster than React re-renders.
  const viewRef = useRef({ scale: 1, pan: { x: 0, y: 0 } });
  viewRef.current = { scale, pan };

  const lastReleasedRef = useRef(null);
  const committedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [availResp, eventResp] = await Promise.all([
          getSeatAvailability(serviceId),
          initialEvent ? Promise.resolve(null) : getServiceById(serviceId),
        ]);
        if (cancelled) return;
        if (availResp?.success) {
          setLayout(availResp.data?.layout ?? null);
          setAvailability(availResp.data?.availability ?? []);
        }
        if (eventResp?.success && eventResp.data) {
          setEvent({
            id: eventResp.data.id,
            title: eventResp.data.name,
            categoryId: eventResp.data.categoryId,
            providerId: eventResp.data.providerId,
            providerServiceId: eventResp.data.id,
          });
        }
      } catch (err) {
        toast.error(err?.response?.data?.message ?? 'Could not load seats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, initialEvent]);

  useEffect(() => {
    if (!hold) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [hold]);

  const holdMsRemaining = hold ? new Date(hold.expiresAt).getTime() - now : 0;
  useEffect(() => {
    if (hold && holdMsRemaining <= 0) {
      toast.error('Your seat hold expired. Please pick again.');
      setHold(null);
      setSelectedSeatIds(new Set());
    }
  }, [hold, holdMsRemaining]);

  useEffect(() => {
    return () => {
      // Don't release if user is continuing to checkout — the booking endpoint
      // needs the hold to still exist so it can attach the seats to the booking.
      if (committedRef.current) return;
      if (hold?.holdToken && lastReleasedRef.current !== hold.holdToken) {
        lastReleasedRef.current = hold.holdToken;
        releaseSeatHold(serviceId, hold.holdToken).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold]);

  const sections = layout?.layoutJson?.sections ?? [];
  const stagePart = layout?.layoutJson?.stage;
  const canvas = layout?.layoutJson?.canvas ?? { width: 1200, height: 800 };

  const availabilityBySeatCode = useMemo(() => {
    const m = new Map();
    for (const a of availability) m.set(a.seatCode, a);
    return m;
  }, [availability]);

  const sectionByVariantId = useMemo(() => {
    const m = new Map();
    for (const s of sections) m.set(s.variantId, s);
    return m;
  }, [sections]);

  // All seats grouped by row label, sorted by seat number — used for BMS-style
  // contiguous auto-selection when the user clicks a seat after picking a count.
  const seatsByRow = useMemo(() => {
    const m = new Map();
    for (const a of availability) {
      const row = a.rowLabel;
      if (row == null) continue;
      if (!m.has(row)) m.set(row, []);
      m.get(row).push(a);
    }
    for (const arr of m.values()) {
      arr.sort(
        (a, b) => Number(a.seatNumber || 0) - Number(b.seatNumber || 0)
      );
    }
    return m;
  }, [availability]);

  const variantStats = useMemo(() => {
    const byVariant = new Map();
    for (const a of availability) {
      if (!byVariant.has(a.variantId)) {
        byVariant.set(a.variantId, { total: 0, available: 0 });
      }
      const s = byVariant.get(a.variantId);
      s.total += 1;
      if (a.status === 'available') s.available += 1;
    }
    const order = [];
    for (const s of sections) {
      if (!order.find((x) => x.variantId === s.variantId)) {
        const stat = byVariant.get(s.variantId) ?? { total: 0, available: 0 };
        const ratio = stat.total > 0 ? stat.available / stat.total : 0;
        let status = 'available';
        if (stat.available === 0) status = 'sold_out';
        else if (ratio <= 0.3) status = 'filling_fast';
        order.push({
          variantId: s.variantId,
          name: s.name?.replace(/\s+[A-Z]\s+[LR]$/, '').trim() ?? s.name,
          color: s.color,
          price: Number(s.price),
          ...stat,
          status,
        });
      }
    }
    return order;
  }, [availability, sections]);

  const summary = useMemo(() => {
    const grouped = {};
    let total = 0;
    for (const seatId of selectedSeatIds) {
      const seat = availability.find((a) => a.id === seatId);
      if (!seat) continue;
      const section = sectionByVariantId.get(seat.variantId);
      if (!section) continue;
      if (!grouped[seat.variantId]) {
        grouped[seat.variantId] = {
          variantId: seat.variantId,
          name: section.name,
          price: Number(section.price),
          color: section.color,
          quantity: 0,
          totalPrice: 0,
          seatLabels: [],
        };
      }
      grouped[seat.variantId].quantity += 1;
      grouped[seat.variantId].totalPrice += Number(section.price);
      const label =
        seat.seatLabel ??
        (seat.rowLabel != null && seat.seatNumber != null
          ? `${seat.rowLabel}-${seat.seatNumber}`
          : null);
      if (label) grouped[seat.variantId].seatLabels.push(label);
      total += Number(section.price);
    }
    const sortLabel = (a, b) => {
      const [ra, na] = a.split('-');
      const [rb, nb] = b.split('-');
      if (ra !== rb) return ra.localeCompare(rb);
      return Number(na || 0) - Number(nb || 0);
    };
    for (const g of Object.values(grouped)) g.seatLabels.sort(sortLabel);
    return { groups: Object.values(grouped), total };
  }, [selectedSeatIds, availability, sectionByVariantId]);

  const fitToScreen = () => {
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const padding = 40;
    const sx = (rect.width - padding) / canvas.width;
    const sy = (rect.height - padding) / canvas.height;
    const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(sx, sy, 1)));
    setScale(next);
    setPan({
      x: (rect.width - canvas.width * next) / 2,
      y: (rect.height - canvas.height * next) / 2,
    });
  };

  useEffect(() => {
    if (!layout) return;
    fitToScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, canvas.width, canvas.height]);

  // Smallest seat across sections, in canvas units — what decides whether the
  // map is currently tappable.
  const baseSeat = useMemo(() => {
    const sizes = sections
      .map((s) => Math.min(Number(s.colSpacing) || 0, Number(s.rowSpacing) || 0) * 0.75)
      .filter((n) => n > 0);
    return sizes.length ? Math.min(...sizes) : 24;
  }, [sections]);

  const coarsePointer = useMemo(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches,
    []
  );
  const needsTapZoom = coarsePointer && baseSeat * scale < TAP_ZOOM_SEAT_PX;

  /** Zoom so seats are ~TAP_ZOOM_TARGET_PX on screen, keeping the tapped spot under the finger. */
  const tapZoomAt = (clientX, clientY) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    const current = viewRef.current.scale;
    const target = Math.min(MAX_SCALE, TAP_ZOOM_TARGET_PX / baseSeat);
    if (target <= current) return;
    zoomBy(target / current, { x: clientX - rect.left, y: clientY - rect.top });
  };

  /**
   * Zoom by `factor`, keeping `pivot` (viewport px) fixed on screen; with no
   * pivot, the centre of the viewport.
   *
   * Computed from `viewRef` rather than nesting setPan inside setScale's
   * updater: React runs updater functions twice in Strict Mode, so the nested
   * pan shift was applied twice and a pivoted zoom overshot, leaving the spot
   * you zoomed into off-screen. The ref also lets rapid wheel ticks chain
   * without waiting for a re-render.
   */
  const zoomBy = (factor, pivot) => {
    const { scale: prev, pan: prevPan } = viewRef.current;
    const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev * factor));
    if (next === prev) return;
    let at = pivot;
    if (!at) {
      const rect = viewportRef.current?.getBoundingClientRect();
      at = rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 };
    }
    const nextPan = {
      x: at.x - ((at.x - prevPan.x) * next) / prev,
      y: at.y - ((at.y - prevPan.y) * next) / prev,
    };
    viewRef.current = { scale: next, pan: nextPan };
    setScale(next);
    setPan(nextPan);
  };

  const handleWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 30) return;
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const pivot = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    zoomBy(e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP, pivot);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-seat="1"]')) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };
  const handleMouseMove = (e) => {
    if (!dragRef.current || dragRef.current.pinch) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.panY + (e.clientY - dragRef.current.startY),
    });
  };
  const handleMouseUp = () => {
    dragRef.current = null;
  };

  const distanceBetween = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const rect = viewportRef.current.getBoundingClientRect();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      dragRef.current = {
        pinch: true,
        startDist: distanceBetween(t1, t2),
        startScale: scale,
        pivot: {
          x: (t1.clientX + t2.clientX) / 2 - rect.left,
          y: (t1.clientY + t2.clientY) / 2 - rect.top,
        },
        panX: pan.x,
        panY: pan.y,
      };
      e.preventDefault();
      return;
    }
    if (e.touches.length === 1) {
      // If the touch starts on a tappable seat, let the button handle the tap.
      // Disabled (held/booked) seats fall through, so tapping them at overview
      // scale still zooms in rather than doing nothing.
      if (e.target.closest('[data-seat="1"]:not(:disabled)')) return;
      const t = e.touches[0];
      dragRef.current = {
        pinch: false,
        startX: t.clientX,
        startY: t.clientY,
        panX: pan.x,
        panY: pan.y,
        moved: false,
      };
    }
  };
  const handleTouchMove = (e) => {
    if (!dragRef.current) return;
    if (dragRef.current.pinch && e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = distanceBetween(t1, t2);
      const factor = dist / dragRef.current.startDist;
      const nextScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, dragRef.current.startScale * factor)
      );
      const pivot = dragRef.current.pivot;
      setScale(nextScale);
      setPan({
        x:
          pivot.x -
          ((pivot.x - dragRef.current.panX) * nextScale) /
            dragRef.current.startScale,
        y:
          pivot.y -
          ((pivot.y - dragRef.current.panY) * nextScale) /
            dragRef.current.startScale,
      });
      return;
    }
    if (!dragRef.current.pinch && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.startX;
      const dy = t.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        dragRef.current.moved = true;
      }
      if (dragRef.current.moved) {
        e.preventDefault();
        setPan({
          x: dragRef.current.panX + dx,
          y: dragRef.current.panY + dy,
        });
      }
    }
  };
  const handleTouchEnd = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    // A tap (no pan, no pinch) on the background or a held seat.
    if (drag && !drag.pinch && !drag.moved && needsTapZoom) {
      tapZoomAt(drag.startX, drag.startY);
    }
  };

  const toggleSeat = (seat) => {
    if (seat.status !== 'available') return;
    if (hold) {
      toast('Release current hold to change selection', { icon: '🔒' });
      return;
    }

    // If the user clicks any seat that's part of the current selection, clear
    // the whole block (BMS behavior).
    if (selectedSeatIds.has(seat.id)) {
      setSelectedSeatIds(new Set());
      return;
    }

    const wanted = Math.max(1, Math.min(HARD_MAX_SEATS, maxSeats ?? 1));

    // BMS-style auto-pick: clicked seat anchors a contiguous block of `wanted`
    // available seats in the same row. Prefer extending to the right; if there
    // aren't enough seats to the right, slide the block left so it ends at the
    // last available seat in the row.
    const rowAll = seatsByRow.get(seat.rowLabel) ?? [];
    const rowFree = rowAll.filter((s) => s.status === 'available');
    const idx = rowFree.findIndex((s) => s.id === seat.id);
    if (idx === -1) {
      setSelectedSeatIds(new Set([seat.id]));
      return;
    }
    let start = idx;
    let end = Math.min(rowFree.length, idx + wanted);
    if (end - start < wanted) {
      start = Math.max(0, end - wanted);
    }
    const picked = rowFree.slice(start, end).map((s) => s.id);
    setSelectedSeatIds(new Set(picked));
  };

  const handleHoldAndContinue = async () => {
    if (!user) {
      openAuthModal?.();
      return;
    }
    if (selectedSeatIds.size === 0) {
      toast.error('Pick at least one seat');
      return;
    }
    setSubmitting(true);
    try {
      const seatIds = [...selectedSeatIds];
      const result = await holdSeats(serviceId, seatIds);
      if (!result?.success) {
        const msg =
          result?.errors?.messages?.[0]?.message ??
          result?.message ??
          'Could not hold seats. Try again.';
        toast.error(msg);
        const fresh = await getSeatAvailability(serviceId);
        if (fresh?.success) setAvailability(fresh.data?.availability ?? []);
        return;
      }
      const { holdToken, expiresAt } = result.data;
      setHold({ holdToken, expiresAt });

      const selectedTickets = summary.groups.map((g) => ({
        id: String(g.variantId),
        name: g.name,
        price: g.price,
        pricePerTicket: g.price,
        quantity: g.quantity,
        totalPrice: g.totalPrice,
        seatLabels: g.seatLabels ?? [],
      }));

      committedRef.current = true;
      navigate(`/book/${serviceId}`, {
        state: {
          event,
          selectedTickets,
          totalAmount: summary.total,
          totalItems: seatIds.length,
          selectedDate,
          selectedTime,
          rawEvent,
          seatIds,
          holdToken,
          holdExpiresAt: expiresAt,
        },
      });
    } catch (err) {
      toast.error(friendlyError(err, 'Could not hold seats'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <main className="lk-container flex-1 space-y-4 py-8" aria-busy="true">
          <Shimmer className="h-6 w-48" />
          <Shimmer className="h-[420px] w-full rounded-2xl" />
          <p className="text-center text-sm text-ink-500">Loading seat layout…</p>
        </main>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <main className="lk-container flex-1 py-16">
          <EmptyState
            icon={AlertTriangle}
            title="Seat selection isn't available for this event"
            message="The organiser hasn't set up a seat map yet. You can still book general admission tickets."
            action={
              <button
                type="button"
                onClick={() =>
                  navigate(`/tickets/${serviceId}`, {
                    replace: true,
                    state: location.state,
                  })
                }
                className="lk-btn-primary"
              >
                Back to ticket selection
              </button>
            }
          />
        </main>
      </div>
    );
  }

  const formatTimer = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const renderSeat = (section, r, c) => {
    const seatNumberStart = Math.max(1, Math.floor(section.seatNumberStart ?? 1));
    const seatNumber = seatNumberStart + c;
    const startLetter = (section.rowLabelStart ?? 'A').toUpperCase();
    const startCode = startLetter.charCodeAt(0);
    const rowLabel =
      section.rowLabels?.[r] ??
      (startCode >= 65 && startCode <= 90
        ? String.fromCharCode(startCode + r)
        : String.fromCharCode(65 + r));
    const seatLabel = `${rowLabel}-${seatNumber}`;
    // Match backend's internal seat_code format (no dash before number) so the
    // availability lookup hits.
    const seatCode = `${section.id}-${rowLabel}${seatNumber}`;
    const seat = availabilityBySeatCode.get(seatCode);

    const cellW = section.colSpacing;
    const cellH = section.rowSpacing;
    const seatSize = Math.min(cellW, cellH) * 0.75;

    if (!seat) {
      return (
        <div
          key={`empty-${section.id}-${r}-${c}`}
          style={{ width: cellW, height: cellH }}
        />
      );
    }

    const isSelected = selectedSeatIds.has(seat.id);
    const isAvailable = seat.status === 'available';
    const isDisabled =
      seat.status === 'booked' || seat.status === 'blocked' || (hold && !isSelected);

    const bg = isSelected
      ? STATUS_COLORS.selected
      : isAvailable
        ? section.color || '#3b82f6'
        : STATUS_COLORS[seat.status] ?? '#6b7280';

    return (
      <div
        key={seat.id}
        style={{
          width: cellW,
          height: cellH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <button
          type="button"
          data-seat="1"
          data-seat-code={seatCode}
          aria-label={`Seat ${seatLabel} — ${seat.status}`}
          disabled={isDisabled || !isAvailable}
          onClick={(e) => {
            if (needsTapZoom) {
              tapZoomAt(e.clientX, e.clientY);
              return;
            }
            toggleSeat(seat);
          }}
          title={`${section.name} · ${seatLabel} · ₹${Number(section.price).toLocaleString()}`}
          style={{
            width: seatSize,
            height: seatSize,
            borderRadius: 4,
            backgroundColor: bg,
            border: isSelected
              ? '2px solid #92400e'
              : '1px solid rgba(0,0,0,0.18)',
            cursor: isAvailable && !hold ? 'pointer' : 'not-allowed',
            opacity:
              seat.status === 'booked' || seat.status === 'blocked' ? 0.45 : 1,
            padding: 0,
            transition: 'transform 0.05s ease',
            transform: isSelected ? 'scale(1.08)' : 'scale(1)',
            color: isSelected || isAvailable ? '#fff' : '#374151',
            fontSize: Math.max(8, Math.min(11, seatSize * 0.32)),
            fontWeight: 600,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            userSelect: 'none',
          }}
        >
          <span style={{ pointerEvents: 'none' }}>{seat.seatLabel ?? seatLabel}</span>
        </button>
      </div>
    );
  };

  const confirmSeatCount = () => {
    setMaxSeats(pendingCount);
    setSeatsModalOpen(false);
    if (selectedSeatIds.size > pendingCount) {
      const next = new Set(Array.from(selectedSeatIds).slice(0, pendingCount));
      setSelectedSeatIds(next);
    }
  };

  const openSeatsModal = () => {
    setPendingCount(maxSeats ?? pendingCount);
    setSeatsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-surface-muted">
      {seatsModalOpen && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-1 text-center">
              <h3 className="text-xl font-semibold text-ink-900">How many seats?</h3>
              <p className="text-xs text-ink-500 mt-1">
                Pick a count, then click any seat to auto-select your block
              </p>
            </div>
            <div className="px-6 pt-3 pb-2 flex items-center justify-center">
              <svg
                viewBox="0 0 220 150"
                width="200"
                height="136"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Theatre seats"
              >
                <defs>
                  <linearGradient id="seatBg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#eff6ff" />
                    <stop offset="1" stopColor="#dbeafe" />
                  </linearGradient>
                  <linearGradient id="seatFront" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#0080ff" />
                    <stop offset="1" stopColor="#0066cc" />
                  </linearGradient>
                </defs>
                <rect width="220" height="150" rx="18" fill="url(#seatBg)" />
                {/* Curved screen */}
                <path
                  d="M 30 22 Q 110 8 190 22 L 190 36 Q 110 22 30 36 Z"
                  fill="#1e3a8a"
                />
                <text
                  x="110"
                  y="32"
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="9"
                  fontWeight="700"
                  letterSpacing="2"
                >
                  STAGE
                </text>
                {/* Light beams */}
                <path d="M 60 36 L 30 90 L 50 90 Z" fill="#ffffff" opacity="0.35" />
                <path d="M 160 36 L 190 90 L 170 90 Z" fill="#ffffff" opacity="0.35" />

                {/* Row 1 — 5 seats (front row, blue) */}
                <g transform="translate(60, 60)">
                  <rect x="0" y="0" width="18" height="14" rx="3" fill="url(#seatFront)" />
                  <rect x="22" y="0" width="18" height="14" rx="3" fill="url(#seatFront)" />
                  <rect x="44" y="0" width="18" height="14" rx="3" fill="url(#seatFront)" />
                  <rect x="66" y="0" width="18" height="14" rx="3" fill="url(#seatFront)" />
                  <rect x="88" y="0" width="18" height="14" rx="3" fill="url(#seatFront)" />
                </g>
                {/* Row 2 — 6 seats */}
                <g transform="translate(50, 85)">
                  <rect x="0" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                  <rect x="22" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                  <rect x="44" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                  <rect x="66" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                  <rect x="88" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                  <rect x="110" y="0" width="18" height="14" rx="3" fill="#3b82f6" />
                </g>
                {/* Row 3 — 7 seats */}
                <g transform="translate(40, 110)">
                  <rect x="0" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="22" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="44" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="66" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="88" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="110" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                  <rect x="132" y="0" width="18" height="14" rx="3" fill="#93c5fd" />
                </g>
              </svg>
            </div>
            <div className="px-4 pb-4 grid grid-cols-10 gap-1.5">
              {Array.from({ length: HARD_MAX_SEATS }).map((_, i) => {
                const n = i + 1;
                const active = pendingCount === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPendingCount(n)}
                    className={`h-9 rounded-full text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-brand-secondary text-white'
                        : 'bg-white text-ink-900 hover:bg-brand-tint border border-ink-200'
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {variantStats.length > 0 && (
              <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {variantStats.map((v) => {
                  const statusLabel =
                    v.status === 'sold_out'
                      ? 'SOLD OUT'
                      : v.status === 'filling_fast'
                        ? 'FILLING FAST'
                        : 'AVAILABLE';
                  const statusColor =
                    v.status === 'sold_out'
                      ? 'text-ink-400'
                      : v.status === 'filling_fast'
                        ? 'text-orange-700'
                        : 'text-green-700'; // -600 shades are ~3:1 on white at this size
                  return (
                    <div
                      key={v.variantId}
                      className="rounded-lg border border-ink-200 px-2 py-2 text-center"
                    >
                      {/* The tier colour is a swatch, not the text colour: admin
                          picks it to match the seat map, and "GOLD" drawn in
                          yellow on white measured ~2:1. */}
                      <div className="flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-900">
                        <span
                          aria-hidden="true"
                          className="h-2.5 w-2.5 flex-none rounded-full ring-1 ring-black/10"
                          style={{ backgroundColor: v.color || '#374151' }}
                        />
                        {v.name}
                      </div>
                      <div className="tabular text-sm font-bold text-ink-900 mt-0.5">
                        ₹{v.price.toLocaleString()}
                      </div>
                      <div className={`text-[11px] font-semibold mt-0.5 ${statusColor}`}>
                        {statusLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={confirmSeatCount}
              className="w-full bg-brand-secondary hover:bg-brand-secondary/90 text-white py-3 font-semibold text-sm tracking-wide"
            >
              Select Seats
            </button>
          </div>
        </div>
      )}
      <div className="px-4 md:px-8 py-4 border-b bg-white sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded hover:bg-surface-sunken"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-base md:text-lg font-semibold text-ink-900">
            {event?.title ?? 'Select Seats'}
          </h1>
          {selectedDate && selectedTime && (
            <p className="text-xs text-ink-500">
              {selectedDate} · {selectedTime}
            </p>
          )}
        </div>
        {maxSeats != null && (
          <button
            type="button"
            onClick={openSeatsModal}
            className="text-xs font-medium text-ink-700 bg-surface-sunken hover:bg-gray-200 px-3 py-1.5 rounded-full"
            title="Change number of seats"
          >
            {selectedSeatIds.size}/{maxSeats} seats
          </button>
        )}
        {hold && (
          <div className="flex items-center gap-1 text-amber-700 bg-amber-50 px-3 py-1 rounded">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium tabular-nums">
              {formatTimer(holdMsRemaining)}
            </span>
          </div>
        )}
      </div>

      <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-500 border-b bg-white">
        <Legend label="Available" colorRing="#3b82f6" hollow />
        <Legend label="Selected" color={STATUS_COLORS.selected} />
        <Legend label="Held" color={STATUS_COLORS.held} />
        <Legend label="Booked" color={STATUS_COLORS.booked} />
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => zoomBy(1 / ZOOM_STEP)}
            className="lk-focus flex h-11 w-11 items-center justify-center rounded-full border bg-white hover:bg-surface-sunken active:scale-95"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="tabular-nums w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => zoomBy(ZOOM_STEP)}
            className="lk-focus flex h-11 w-11 items-center justify-center rounded-full border bg-white hover:bg-surface-sunken active:scale-95"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={fitToScreen}
            className="lk-focus flex h-11 w-11 items-center justify-center rounded-full border bg-white hover:bg-surface-sunken active:scale-95"
            aria-label="Fit to screen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={viewportRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          position: 'relative',
          width: '100%',
          // dvh, not vh: on iOS Safari 100vh includes the area behind the URL
          // bar, so the map ran taller than the visible screen.
          height: 'calc(100dvh - 220px)',
          overflow: 'hidden',
          background: '#f3f4f6',
          cursor: dragRef.current ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        {needsTapZoom && (
          <p
            aria-live="polite"
            className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink-900/85 px-3.5 py-1.5 text-xs font-semibold text-white shadow-card"
          >
            Tap an area to zoom in, then pick your seats
          </p>
        )}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: canvas.width,
            height: canvas.height,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            background: '#fafafa',
            border: '1px dashed #d1d5db',
            borderRadius: 4,
          }}
        >
          {stagePart && (
            <div
              style={{
                position: 'absolute',
                left: stagePart.x,
                top: stagePart.y,
                width: stagePart.width,
                height: stagePart.height,
                background: '#1f2937',
                color: 'white',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}
            >
              {stagePart.label ?? 'STAGE'}
            </div>
          )}

          {sections.map((section) => {
            // Show a row letter once per row, on the section that holds seat #1
            // for that row. This keeps the BMS-style "row letter on the left"
            // without duplicating it on every section.
            const showRowLetter = (section.seatNumberStart ?? 1) === 1;
            const rowLetter =
              section.rowLabels?.[0] ?? (section.rowLabelStart ?? 'A');
            return (
            <div
              key={section.id}
              style={{
                position: 'absolute',
                left: section.origin.x,
                top: section.origin.y,
              }}
            >
              {showRowLetter && (
                <div
                  style={{
                    position: 'absolute',
                    left: -22,
                    top: (section.rowSpacing ?? 30) / 2 - 8,
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  {rowLetter}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {Array.from({ length: section.rows }).map((_, r) => {
                  const colsThisRow = Math.max(
                    1,
                    Math.floor(
                      (section.columnsPerRow?.[r] ?? section.columns) || section.columns
                    )
                  );
                  const justify =
                    section.alignment === 'left'
                      ? 'flex-start'
                      : section.alignment === 'right'
                        ? 'flex-end'
                        : 'center';
                  return (
                    <div
                      key={`row-${section.id}-${r}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: section.rowSpacing,
                        justifyContent: justify,
                      }}
                    >
                      {Array.from({ length: colsThisRow }).map((_, c) =>
                        renderSeat(section, r, c)
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* The seat summary + Continue button. Plain CSS entrance: this bar
          holds the only way forward, so it must not start at opacity 0. */}
      <div className="fixed bottom-0 inset-x-0 z-40 animate-floatUp border-t border-ink-200 bg-white pb-[env(safe-area-inset-bottom)] shadow-bar">
        <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2 flex-1 min-w-0">
            {summary.groups.length === 0 ? (
              <span className="text-sm text-ink-500">No seats selected</span>
            ) : (
              summary.groups.map((g) => (
                <div
                  key={g.variantId}
                  className="flex items-center gap-2 bg-surface-muted rounded px-2 py-1"
                >
                  <span
                    className="inline-block w-3 h-3 rounded"
                    style={{ background: g.color }}
                  />
                  <span className="text-sm font-medium">
                    {g.name} × {g.quantity}
                  </span>
                  <span className="text-xs text-ink-500">
                    ₹{g.totalPrice.toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-ink-500">Total</div>
              <div className="text-lg font-semibold">
                ₹{summary.total.toLocaleString()}
              </div>
            </div>
            <button
              onClick={handleHoldAndContinue}
              disabled={selectedSeatIds.size === 0 || submitting || !!hold}
              className="px-5 py-2 rounded bg-brand-secondary text-white font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Holding…' : hold ? 'Continuing…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Legend = ({ label, color, colorRing, hollow }) => (
  <div className="flex items-center gap-1">
    <span
      className="inline-block w-3 h-3 rounded-full"
      style={{
        background: hollow ? 'transparent' : color,
        border: hollow ? `2px solid ${colorRing}` : '1px solid rgba(0,0,0,0.2)',
      }}
    />
    <span>{label}</span>
  </div>
);

export default SeatSelection;
